import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
// --- Import components
import PaymentMethodsListItem from "../PaymentMethodsListItem/PaymentMethodsListItem";
import Preloader from "../../../../components/UI/Preloader/Preloader";
import ModalWindow from "../../../../components/UI/ModalWindow/ModalWindow";
import NotificationError from "../../../../components/UI/NotificationError/NotificationError";
// --- Import hooks
import useMakeRequest from "../../../../hooks/useMakeRequest";

import "./PaymentMethodsList.sass";

function getDefaultSelectedCard(arr) {
  let res = arr.filter((item) => {
    return item.state === 1;
  })[0];
  if (!res) {
    res = arr[0];
  }
  return res;
}

function cardIsExist(arr, target) {
  return arr.some((item) => {
    if (item.cc_last_four_digits === target) {
      return true;
    }
    return false;
  });
}

function PaymentMethodsList({ shouldReload, expectedCard, resetExpectedCard }) {
  const [setParams, result, isLoading, hasError, error] = useMakeRequest();
  const [setParamsSelect] = useMakeRequest();
  const [setParamsDelete, deleteResult, deleteIsLoading] = useMakeRequest();

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState();
  const [expectedCardExist, setExpectedCard] = useState(false);

  const [requestCounter, setRequestCounter] = useState(0);

  const getNextCard = useCallback(
    (target) => {
      const nextIndex = 1 + result.indexOf(target);
      let res = result[nextIndex];
      if (nextIndex > result.length - 1) {
        // if it is the last element in the array
        res = result[0];
      }
      return res;
    },
    [result]
  );

  function handleSelectedCard(cardData) {
    setPaymentMethod(cardData);
    setParamsSelect({
      c: "accountStore",
      a: "creditCardSetMain",
      p: {
        id: cardData.id,
        last_four_digits: cardData.cc_last_four_digits,
      },
    });
  }

  function deletePaymentMethod({ cardData, isLast }) {
    if (!isLast && cardData.state === 1) {
      // when trying to delete the selected card
      handleSelectedCard(getNextCard(cardData));
    }
    setParamsDelete({
      c: "accountStore",
      a: "creditCardDelete",
      p: {
        id: cardData.id,
        last_four_digits: cardData.cc_last_four_digits,
      },
    });
  }

  // ---- Make request for data on first render
  useEffect(() => {
    setParams({
      c: "accountStore",
      a: "userCreditCards",
    });
  }, []);

  useEffect(() => {
    if (shouldReload) {
      setParams({
        c: "accountStore",
        a: "userCreditCards",
      });
    }
  }, [shouldReload]);

  // --- Make reqest for data after deleting
  useEffect(() => {
    if (!deleteIsLoading && deleteResult) {
      setParams({
        c: "accountStore",
        a: "userCreditCards",
      });
    }
  }, [deleteResult, deleteIsLoading]);

  // --- Set default credit card
  useEffect(() => {
    let timer;
    if (result) {
      if (
        expectedCard &&
        requestCounter <= 10 &&
        !cardIsExist(result, expectedCard)
      ) {
        // when adding a new card, make a request until this card appears
        setExpectedCard(false);
        timer = setTimeout(() => {
          setParams({
            c: "accountStore",
            a: "userCreditCards",
          });
          setRequestCounter(requestCounter + 1);
        }, 1000);
      } else {
        setExpectedCard(true);
        resetExpectedCard();
        setRequestCounter(0);
      }
    }
    return () => {
      clearTimeout(timer);
    };
  }, [result, isLoading, expectedCard, requestCounter]);

  useMemo(() => {
    if (result) {
      setPaymentMethod(getDefaultSelectedCard(result));
    }
  }, [result]);

  // ---- Throw Error if we has one for ErrorBoundary
  if (hasError && error !== "") {
    throw new Error(error);
  }

  return (
    <>
      {result !== null && result.length === 0 ? (
        <NotificationError
          classWrapper="payment-section__error"
          classText="payment-section__error-text"
          errorMessage="You have no card"
        />
      ) : null}
      <div className="col-12">
        <ul className="row flex-column align-items-start payment-methods-list">
          {result &&
            paymentMethod &&
            result.map((item, i) => {
              return (
                <PaymentMethodsListItem
                  key={i}
                  id={item.id}
                  cardData={item}
                  cardType={item.cc_card_type}
                  lastFourDigits={item.cc_last_four_digits}
                  exp={item.cc_exp_date}
                  lastBillingTime={item.last_billing_time}
                  currentCard={paymentMethod}
                  handleSelectedCard={(cardData) =>
                    handleSelectedCard(cardData)
                  }
                  deletePaymentMethod={(cardData) => {
                    setOpenDeletePopup(true);
                    setDeleteTarget({
                      cardData,
                      isLast: i === result.length - 1,
                    });
                  }}
                />
              );
            })}
        </ul>
      </div>

      {!expectedCardExist && (
        <Preloader className="payment-methods-list__loading" />
      )}
      <ModalWindow
        isOpen={openDeletePopup}
        onCancel={() => {
          setDeleteTarget(null);
          setOpenDeletePopup(false);
        }}
        title="Are you sure you want to remove this credit card?"
      >
        <div className="delete-cc-confirm__row">
          <button
            className="delete-cc-confirm__button"
            onClick={() => {
              deletePaymentMethod(deleteTarget);
              setDeleteTarget(null);
              setOpenDeletePopup(false);
            }}
          >
            Yes
          </button>
          <button
            className="delete-cc-confirm__button"
            onClick={() => {
              setDeleteTarget(null);
              setOpenDeletePopup(false);
            }}
          >
            No
          </button>
        </div>
      </ModalWindow>
    </>
  );
}

PaymentMethodsList.propTypes = {
  shouldReload: PropTypes.bool.isRequired,
  expectedCard: PropTypes.string,
  resetExpectedCard: PropTypes.func.isRequired,
};
PaymentMethodsList.defaultProps = {
  expectedCard: null,
};

export default PaymentMethodsList;
